import blackTea from "@/assets/black-tea.webp";
import whiteTea from "@/assets/white-tea.webp";
import blendedTea from "@/assets/blended-tea.webp";
import greenTea from "@/assets/green-tea.webp";
import herbalTea from "@/assets/herbal-tea.webp";
import productBlackBags from "@/assets/product-black-tea-bags.webp";
import productBluePea from "@/assets/product-blue-pea.webp";
import productHibiscus from "@/assets/product-hibiscus.webp";
import productChamomile from "@/assets/product-chamomile.webp";
import productRoseGreen from "@/assets/product-rose-green.webp";

export type TeaCategory = "Black" | "Blended" | "Flavoured" | "Artisanal";

export type Tea = {
  slug: string;
  name: string;
  kind: string;
  category: TeaCategory;
  image: string;
  notes: string;
  origin: string;
  body: string;
  story: string;
  grades: string[];
  brewing: { leaf: string; water: string; time: string; note: string };
};

export const teas: Tea[] = [
  {
    slug: "ceylon-sovereign",
    name: "Ceylon Sovereign",
    kind: "Black Tea",
    category: "Black",
    image: blackTea,
    notes: "Malt · Cocoa · Dried Fig",
    origin: "Nuwara Eliya, 1,900m",
    body: "Our flagship — a full-bodied orthodox black tea with a deep amber liquor and a lingering finish of stone fruit and warm cedar.",
    story:
      "Sovereign is plucked from a single garden above Kandapola, where cold nights slow the leaf and concentrate its character. Sixteen hours of withering, a patient oxidation, and a brief firing give it the malt-and-cocoa depth our house is known for.",
    grades: ["OP1 · Golden Tip", "FBOP", "Pekoe"],
    brewing: {
      leaf: "2.5 g per cup",
      water: "95°C, freshly drawn",
      time: "3–4 minutes",
      note: "Takes a drop of milk gracefully, but is composed enough to stand alone.",
    },
  },
  {
    slug: "silver-whisper",
    name: "Silver Whisper",
    kind: "White Tea",
    category: "Artisanal",
    image: whiteTea,
    notes: "Honeysuckle · Melon · Silk",
    origin: "Uva Highlands, 2,100m",
    body: "Only the youngest silver-tipped buds, plucked once a year and gently withered in the sun. A tea of restraint and light.",
    story:
      "One harvest, one garden, one gesture — the unopened bud, taken before the mist lifts and dried on bamboo in the morning sun. Nothing is rolled, nothing is rushed. What remains is the quietest tea we make.",
    grades: ["Silver Needle · First flush"],
    brewing: {
      leaf: "3 g per cup",
      water: "80°C — never boiling",
      time: "4–5 minutes, up to three infusions",
      note: "Use soft water if you can; the tea rewards it with a silkier body.",
    },
  },
  {
    slug: "rose-cardamom-noir",
    name: "Rose Cardamom Noir",
    kind: "Blended Tea",
    category: "Blended",
    image: blendedTea,
    notes: "Damask Rose · Green Cardamom · Bergamot",
    origin: "Blended in small copper pans",
    body: "A single-estate black tea infused with hand-crushed cardamom, Persian rose petals, and cold-pressed Calabrian bergamot oil.",
    story:
      "Built at the copper table in batches of forty-eight tins, Noir is our blender's homage to the old spice route — Ceylon leaf as the canvas, rose and cardamom as the brushwork, a thread of bergamot to carry the finish.",
    grades: ["FBOP base · Small batch"],
    brewing: {
      leaf: "2.5 g per cup",
      water: "95°C",
      time: "3 minutes",
      note: "Best taken without milk, late in the afternoon.",
    },
  },
  {
    slug: "highland-green",
    name: "Highland Green",
    kind: "Green Tea",
    category: "Artisanal",
    image: greenTea,
    notes: "Grassy · Bright · Mineral",
    origin: "Kandy Estate, 1,400m",
    body: "Delicate pan-fired greens with a clean mineral finish, from gardens on the cooler eastern slopes.",
    story:
      "Pan-fired the same morning it is plucked, Highland Green keeps the snap of the fresh leaf — cut grass, mountain water, a faint sweetness that arrives only after the cup has cooled a little.",
    grades: ["Pan-fired · Young Hyson"],
    brewing: {
      leaf: "2 g per cup",
      water: "75–80°C",
      time: "2–3 minutes, up to two infusions",
      note: "Over-steeping turns it bitter; take it out early and pour again.",
    },
  },
  {
    slug: "black-tea-bags",
    name: "Ceylon Black — Pyramid Bags",
    kind: "Bags · Ceylon Orthodox",
    category: "Black",
    image: productBlackBags,
    notes: "Malt · Cocoa · Amber",
    origin: "Nuwara Eliya · 1,900m",
    body: "Full-bodied single-estate Ceylon black in silken pyramid bags. Bright liquor, malty depth, a long cocoa finish.",
    story:
      "The same Sovereign-grade leaf, uncut and unbroken, in a biodegradable pyramid that gives it room to open. For mornings when ceremony must fit inside four minutes.",
    grades: ["Whole-leaf pyramid · 15 / 50 / 100 count"],
    brewing: {
      leaf: "1 pyramid per cup",
      water: "95°C",
      time: "3–4 minutes",
      note: "One bag stands two steepings if you like a lighter second cup.",
    },
  },
  {
    slug: "blue-pea",
    name: "Blue Pea Tea",
    kind: "Bags · Butterfly Pea",
    category: "Flavoured",
    image: productBluePea,
    notes: "Earthy · Floral · Indigo",
    origin: "Central Province lowlands",
    body: "Vivid indigo infusion from wildcrafted butterfly pea blossoms. Caffeine-free, gentle, and changes colour with lemon.",
    story:
      "Whole butterfly-pea blossoms, gathered by hand from village hedgerows and dried the same day. The cup pours a startling indigo — add a squeeze of lemon and watch it turn violet.",
    grades: ["Whole blossom · Caffeine-free"],
    brewing: {
      leaf: "1 bag or 5–6 blossoms",
      water: "95°C",
      time: "4–5 minutes",
      note: "Serve iced with lime for the full colour turn.",
    },
  },
  {
    slug: "hibiscus",
    name: "Hibiscus Tea",
    kind: "Loose · Ruby Petals",
    category: "Flavoured",
    image: productHibiscus,
    notes: "Tart · Berry · Crimson",
    origin: "Wildcrafted · Southern coast",
    body: "Sun-dried hibiscus calyces steeped into a deep ruby cup. Refreshing served hot at dawn or iced through the afternoon.",
    story:
      "Roselle calyces split and sun-dried on woven mats by the southern coast. The liquor is crimson, bracing, and tart as young berries — a caffeine-free cup with real spine.",
    grades: ["Whole calyx · Caffeine-free"],
    brewing: {
      leaf: "3 g per cup",
      water: "95°C",
      time: "5 minutes",
      note: "A spoon of honey rounds the tartness; over ice it is fearless.",
    },
  },
  {
    slug: "chamomile",
    name: "Chamomile Tea",
    kind: "Loose · Egyptian Blossom",
    category: "Flavoured",
    image: productChamomile,
    notes: "Apple · Honey · Calm",
    origin: "Nile Valley · Single origin",
    body: "Whole chamomile flower heads, slowly air-dried to preserve the delicate apple-honey notes. A tea for slow evenings.",
    story:
      "Whole flower heads — never dust, never stems — air-dried low and slow so the apple-honey sweetness survives the journey to the cup. The last pot of the day, poured without hurry.",
    grades: ["Whole flower · Caffeine-free"],
    brewing: {
      leaf: "2 g per cup",
      water: "95°C",
      time: "5 minutes, covered",
      note: "Cover the cup while it steeps; the aroma is half the medicine.",
    },
  },
  {
    slug: "rose-green",
    name: "Rose Green Tea",
    kind: "Loose · Rose & Sencha",
    category: "Blended",
    image: productRoseGreen,
    notes: "Damask Rose · Grassy · Silk",
    origin: "Kandy Estate · Small batch",
    body: "Pan-fired Ceylon green leaves layered with hand-plucked Damask rose petals. Soft, aromatic, quietly romantic.",
    story:
      "Green leaf and rose petal are layered in alternating beds and left to share a room overnight, the old way — no oils, no sprays. What the leaf remembers of the rose is what you taste.",
    grades: ["Pan-fired green · Scented, small batch"],
    brewing: {
      leaf: "2 g per cup",
      water: "80°C",
      time: "2–3 minutes",
      note: "Gentle heat keeps the rose from turning soapy.",
    },
  },
  {
    slug: "herbal-infusions",
    name: "Herbal Infusions",
    kind: "Caffeine-free · Restorative",
    category: "Flavoured",
    image: herbalTea,
    notes: "Botanical · Wildcrafted · Calm",
    origin: "Island-wide · Seasonal",
    body: "Wildcrafted herbs and blossoms, gently steeped — lemongrass, ginger, moringa, and whatever the season offers.",
    story:
      "A rotating cabinet of island botanicals, gathered in small quantities when they are at their best. No two seasons carry quite the same shelf.",
    grades: ["Seasonal · Caffeine-free"],
    brewing: {
      leaf: "2–3 g per cup",
      water: "95°C",
      time: "5 minutes",
      note: "Each tin carries its own steeping card.",
    },
  },
];

export const getTea = (slug: string) => teas.find((t) => t.slug === slug);
